import React from 'react';
import { useTranslation } from './i18n.js';

const STORAGE_KEY = 'prosveta-cards-collapsed';
const OPEN_EVENT = 'prosveta:open-card';

// Demande l'ouverture d'un cadre (ex. clic dans le sommaire, voir sectionNav.js). Le cadre reste
// ensuite déplié, comme s'il avait été déplié à la main.
export function openCard(id) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { id } }));
}

// Le rendu serveur et la première passe client affichent tous les cadres dépliés : l'état
// mémorisé n'est relu qu'après hydratation (effet), pour que le HTML initial reste identique des
// deux côtés.
function readStored() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

function writeStored(id, collapsed) {
  try {
    const all = readStored();
    all[id] = collapsed;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // stockage indisponible (navigation privée, données bloquées) : le cadre reste utilisable
  }
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <circle cx="12" cy="6" r="4" fill="currentColor" />
      <rect x="10.5" y="10" width="3" height="8" rx="1" fill="currentColor" />
      <line x1="12" y1="18" x2="12" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function CollapsibleCard({ id, label, as: Tag = 'section', className = '', children, ...rest }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = React.useState(false);

  React.useEffect(() => {
    // Arrivée directe sur /fr/#collection : le cadre visé est déplié même s'il avait été replié.
    const targeted = window.location.hash === `#${id}`;
    if (targeted && readStored()[id] === true) {
      writeStored(id, false);
    }
    setCollapsed(!targeted && readStored()[id] === true);

    const onOpen = (event) => {
      if (event.detail && event.detail.id === id) {
        setCollapsed(false);
        writeStored(id, false);
      }
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [id]);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeStored(id, next);
  };

  const hint = collapsed ? t('cardUnpinnedHint') : t('cardPinnedHint');

  return (
    <Tag id={id} className={`${className} collapsible-card${collapsed ? ' is-collapsed' : ''}`} {...rest}>
      <button
        type="button"
        className={`card-pin ${collapsed ? 'is-unpinned' : 'is-pinned'}`}
        aria-expanded={!collapsed}
        aria-label={hint}
        onClick={toggle}
      >
        <PinIcon />
        <span className="card-pin__bubble" aria-hidden="true">
          {hint}
        </span>
      </button>
      {collapsed ? <p className="card-collapsed-label">{label}</p> : children}
    </Tag>
  );
}
